import { Picture } from '@app/components/Picture';
import type { HeroBanner as HomePageHeroBanner } from '@app/data/contentful';
import { Box, Button, Card, Text } from '@redbubble/design-system';
import * as constants from '@redbubble/design-system/react/constants';
import { UserInfo } from '@redbubble/next/types';
import { AnalyticsPromotionCreativeSlot } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  trackGA4SelectPromotionHomePage,
  trackGA4ViewPromotionHomePage,
} from '../../analytics';
import { getUrlWithLocale } from '../../utils/getUrlWithLocale';
import { useContentfulImage } from '../../utils/useContentfulImage';
import { getColor } from './colorDictionary';
import styles from './HeroBanner.module.scss';
import { desktopImageSizes, mobileImageSizes } from './imageSizes';

export interface HeroPannerProps {
  herobanner: HomePageHeroBanner;
}

export const HeroBanner: React.FC<HeroPannerProps> = ({ herobanner }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
  });
  const [{ locale }] = useUserInfo<UserInfo>();

  const handleClick = () => {
    trackGA4SelectPromotionHomePage({
      promotionName: herobanner.taglineText ?? '',
      creativeSlot: AnalyticsPromotionCreativeSlot.HeroBanner,
    });
  };

  useEffect(() => {
    if (inView) {
      trackGA4ViewPromotionHomePage({
        promotionName: herobanner.taglineText ?? '',
        creativeSlot: AnalyticsPromotionCreativeSlot.HeroBanner,
      });
    }
  }, [inView, herobanner.taglineText]);

  const {
    buttonText,
    path,
    textColor,
    taglineText,
    titleText,
    desktopImage,
    mobileImage,
  } = herobanner;

  const desktopImageProps = useContentfulImage(
    desktopImageSizes,
    desktopImage?.url
  );

  const mobileImageProps = useContentfulImage(
    mobileImageSizes,
    mobileImage?.url
  );

  const hasCallToActionButton = !!buttonText && !!path;

  return (
    <Card transparent overflowHidden={false} ref={ref}>
      <Box className={styles.heroBanner}>
        <div className={styles.hero}>
          <div
            className={styles.heroImageContainer}
            style={{ backgroundColor: getColor(textColor) }}
          >
            <Picture roundedCorners fillContainer data-testid="heroBannerImage">
              <Picture.Source
                srcSet={desktopImageProps.srcSet}
                type="image/webp"
                media="(min-width: 769px)"
                sizes={desktopImageProps.sizes}
                testId="heroBannerImage"
              />
              <Picture.Source
                srcSet={mobileImageProps.srcSet}
                type="image/webp"
                media="(max-width: 768px)"
                sizes={mobileImageProps.sizes}
                testId="heroBannerMobileImage"
              />
              <Picture.Img src={desktopImageProps.src} alt="banner" />
            </Picture>
          </div>
          <div
            className={styles.heroContent}
            style={{ color: getColor(textColor) }}
            data-testid="heroBanner"
          >
            {titleText && (
              <div className={styles.illustrationContainer}>
                <Text
                  className={styles.title}
                  element="h1"
                  type="display1"
                  block
                >
                  {titleText}
                </Text>
              </div>
            )}
            {taglineText && <div className={styles.tagLine}>{taglineText}</div>}
            {hasCallToActionButton && (
              <Box marginTop="xl">
                <Button
                  href={getUrlWithLocale(locale, path)}
                  intent={constants.NEUTRAL}
                  strong
                  elevation={constants.ELEVATION_MEDIUM}
                  size={constants.LARGE}
                  onClick={handleClick}
                >
                  <Text type="display3">{buttonText}</Text>
                </Button>
              </Box>
            )}
          </div>
        </div>
      </Box>
    </Card>
  );
};
